import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getTenantId, RoBTenantRolesScopes } from 'src/Common/Models/StoreConstants';
import { UATConstants } from 'src/Common/Models/UATConstants';
import { IGetRecommendedOffersResponse } from 'src/manageAction/components/molecules/AMM/NBAR/type';
import {
  IArtemisApiResponse,
  ICommentProperty,
  IRaidPayload,
  IGetUniqueTPIDsResponse,
} from 'src/manageAction/interfaces/ArtemisAction';
import { IRoBCommentItem } from 'src/manageAction/interfaces/CommentsForRoB';
import { MCAPSLeadershipROB } from 'src/manageAction/interfaces/MCAPSLeadershipROB';
import {
  MANAGE_ACTION_API_CONSTANTS,
  MANAGE_ACTION_CONSTANTS,
  MANAGE_ACTION_FIELDS,
} from 'src/manageAction/utils/manageActionConstants';

export interface IAONAcrAndRoiDetailsPayload {
  id: string;
  raidId: number;
  partitionKey: string;
  OpportunityId: string;
  ProjectStartDate: string;
  SelectedMilestoneIds: string[];
  RequestedAmount: number;
  ProjectScopeDistribution:number;
  IsComboOffer: boolean;
}

export interface IAONMileStoneResponse {
  MileStoneNumber: string;
  MileStoneName: string;
  WorkLoadName: string;
  WorkLoadType: string;
  OpportunityGUID: string;
  MileStoneDate: string;
  MonthlyUseBase: string;
  MileStoneStatus: string;
  ACRCalculation: number;
  ECIFACRCalculation: number;
  ACOACRCalculation: number;
  ACRCalculation3Year: number;
  OfferEligibility: string;
  SalesStage: string;
  SegmentGroup: string;
  SubSegment: string;
  MileStoneCategory: string;
  CustomerCommitment: string;
  Program: string;
  NoOfMonths: number;
}

export interface IAONRecommendedOffer {
  OfferId: number;
  ProgramId: number;
  OfferName: string;
  ProjectSize: string;
  DeliveryPartnerEligibility: string;
  TechnicalScenarios?: string;
  OfferConsiderations: string;
  MileStonesIDs: string;
  Sequence: number;
  IsOfferSupported: boolean;
  IsEcif: boolean;
  OfferType: string;
  SalesType: string;
  OneYearThreshold?: number;
  ThreeYearThreshold?: number;
  OneYearROI: number | null;
  ThreeYearROI: number | null;
  Link: string;
  OfferTitle: string;
  ProgramType: string;
  SupportAlias?: string;
  IsComboOffer: boolean;
  MaxAllowedFunds?: number;
  RestrictedAmount?: number;
  IsAttestationNeeded: boolean;
  SubmissionMessage: string;
  IsException: boolean;
  OfferLink: string;
}

export interface IAONAcrAndRoiDetailsResponse {
  TotalACR: number | null;
  Total3YearACR: number | null;
  TotalOfferEligibleACR: number | null;
  OneYearROI: number | null;
  ThreeYearROI: number | null;
  IncrementalRevenueThreeYear: Record<string, number> | null;
  MileStonesResponses: IAONMileStoneResponse[];
  RecommendedOffers: IAONRecommendedOffer[];
  UpdatedProjectStartDate: string | null;
}

export interface IUploadSasUrlResponse {
  sasUrl: string;
  blobPath: string;
  attachmentId: number;
  expiresOn?: string;
}

export interface IDownloadSasUrlResponse {
  sasUrl: string;
  expiresOn?: string;
}

export const getRaidEntityApi = createApi({
  reducerPath: 'getRaidEntityApi',
  baseQuery: fetchBaseQuery({
    baseUrl: UATConstants.CommonServicesWebApiUrl,
    prepareHeaders: (headers) => {
      const token = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_API);
      const coreAPIToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_CORE_API);
      const graphToken = sessionStorage.getItem(MANAGE_ACTION_CONSTANTS.JWT_TOKEN_FOR_GRAPH);

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
        // headers.set('Coreapitoken', coreAPIToken);
        // headers.set('Graphtoken', graphToken);
      }
      return headers;
    },
  }),
  tagTypes: ['getComments', 'getRaidEntity'],
  endpoints: (builder) => ({
    getRaidEntityData: builder.query<any, IRaidPayload>({
      query: (raidPayload) => {
        if (MANAGE_ACTION_FIELDS.RoBTenantId == raidPayload.tenantID) {
          return {
            method: 'POST',
            url: `${MANAGE_ACTION_API_CONSTANTS.GetRaidEntity}${raidPayload.queryString}`,
            body: { RoleScopeMap: raidPayload.RoleScopeMap },
          };
        } else {
          return {
            method: 'GET',
            url: `${MANAGE_ACTION_API_CONSTANTS.GetRaidEntity}${raidPayload.queryString}`,
          };
        }
      },
      providesTags: ['getRaidEntity'],
    }),
    updateRaidEntityData: builder.mutation<any[], MCAPSLeadershipROB>({
      query: (updateObject) => {
        let url: string = `${MANAGE_ACTION_API_CONSTANTS.UpdateRaidEntity}?tenantid=${updateObject.tenantid}`;

        if (updateObject.pk) {
          url += `&partitionkey=${updateObject.pk}`;
        }

        if (updateObject.id) {
          url += `&id=${updateObject.id}`;
        }

        return {
          method: 'POST',
          url,
          body: updateObject,
        };
      },
      invalidatesTags: (result, error) => {
        // Only invalidate tags if the mutation was successful
        return result ? ['getRaidEntity', 'getComments'] : ['getComments'];
      },
    }),
    updateRaidComments: builder.mutation<any[], string[]>({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.UpdateComment}?tenantid=${payload.tenantid}`,
        body: payload,
      }),
      invalidatesTags: (result) => {
        return result ? ['getComments'] : [];
      },
    }),
    deleteRaidComments: builder.mutation<any[], string[]>({
      query: (payload) => ({
        method: 'DELETE',
        url: `${MANAGE_ACTION_API_CONSTANTS.DeleteComment}?tenantid=${payload.tenantid}`,
        body: payload,
      }),
      invalidatesTags: ['getComments'],
    }),
    uploadArtemisAttachments: builder.mutation<unknown, { tenantid: string; formData: FormData }>({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.UploadArtemisAttachments}?tenantid=${payload.tenantid}`,
        body: payload.formData,
      }),
    }),
    getUploadSasUrl: builder.mutation<IUploadSasUrlResponse, { raidId: string; fileName: string }>({
      query: ({ raidId, fileName }) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetUploadSasUrl}?raidId=${encodeURIComponent(
          raidId
        )}&fileName=${encodeURIComponent(fileName)}`,
      }),
    }),
    getDownloadSasUrl: builder.mutation<IDownloadSasUrlResponse, { blobPath: string }>({
      query: ({ blobPath }) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetDownloadSasUrl}?blobPath=${encodeURIComponent(blobPath)}`,
      }),
    }),
    downloadArtemisAttachment: builder.mutation<unknown, unknown>({
      query: (urlParam) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.DownloadArtemisAttachments}/${urlParam}`,
      }),
    }),
    createRaidComment: builder.mutation<unknown, ICommentProperty>({
      query: (payload) => ({
        url: `${MANAGE_ACTION_API_CONSTANTS.CreateComment}?tenantid=${payload.tenantid}`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['getComments'],
    }),
    getComments: builder.query<IRoBCommentItem[], string>({
      query: (raidid) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetComments}${raidid}`,
      }),
      providesTags: ['getComments'],
    }),
    getRaidHistory: builder.query<unknown, unknown>({
      query: (raidid) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetRaidHistory}${raidid}&tenantid=${getTenantId()}`,
      }),
    }),
    getArtemisTags: builder.query<unknown, unknown>({
      query: (searchText: string) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetArtemisTags}?$search=${encodeURIComponent(searchText)}&OrderBy=name asc`,
      }),
    }),
    getFollowing: builder.query<string, { workItemID: string; userid: string }>({
      query: (getFollowingData) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.followActionRoot}/${getFollowingData?.workItemID}/${MANAGE_ACTION_API_CONSTANTS.GetFollowing}`,
      }),
      // Add these options to ensure consistent behavior
      transformResponse: (response: any) => {
        console.log('getFollowing response:', response);
        // Ensure consistent response format
        return response === null || response === undefined ? '0000' : response;
      },
      transformErrorResponse: (error) => {
        // Handle error response
        // console.error('getFollowing error:', error);
        return '0000'; // Return default value on error
      },
      // Force refetch on each call
      forceRefetch: ({ currentArg, previousArg }) => {
        return true;
      },
    }),
    addFollowing: builder.query<string, { workItemID: string; userid: string }>({
      query: (addFollowing) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.followActionRoot}/${addFollowing?.workItemID}/${MANAGE_ACTION_API_CONSTANTS.AddFollowing}`,
      }),
    }),
    deleteFollowing: builder.query<string, { workItemID: string; userid: string }>({
      query: (deleteFollowing) => ({
        method: 'DELETE',
        url: `${MANAGE_ACTION_API_CONSTANTS.followActionRoot}/${deleteFollowing?.workItemID}/${MANAGE_ACTION_API_CONSTANTS.DeleteFollowing}`,
      }),
    }),
    fetchDetailsForArtemisBasedOnAdo: builder.query<unknown, { id: string }>({
      query: (payload) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.ArtemisBasedOnAdo}?$filter=blockerid eq ${payload.id}`,
      }),
    }),
    fetchDetailsForAdoBasedOnArtemis: builder.query<unknown, { raidId: string; tenantId: string }>({
      query: (payload) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.AdoBasedOnArtemis}?$filter=raidid eq ${payload.raidId} and tenantid eq '${payload.tenantId}'`,
      }),
    }),
    GetIsRestrictedUser: builder.query<boolean, string>({
      query: (opportunityId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetIsRestrictedUser}${opportunityId}`,
      }),
    }),
    GetIsRestrictedUserForPIIFields: builder.query<boolean, string>({
      query: () => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetIsRestrictedUserForPIIFields}`,
      }),
    }),
    getRaidItemById: builder.query<
      unknown,
      {
        raidItemId: string;
        partitionKey: string;
        queryId: string;
        tenantId: string;
        raidId: string;
      }
    >({
      query: (payload) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetRaidEntity}(${payload.raidItemId})?partitionKey=${payload.partitionKey}&queryid=${payload.queryId}&tenantid=${payload.tenantId}&raidid=${payload.raidId}&$select=raidid,id,pk`,
      }),
      providesTags: ['getRaidEntity'],
    }),
    // NBAR Endpoints
    getWorkloads: builder.query<any, string>({
      query: (raidId) => ({
        method: 'GET',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetWorkloads}/${raidId}`,
      }),
    }),
    getRecommendedOffers: builder.mutation<
      IGetRecommendedOffersResponse,
      { selectedWorkloads: string[] }
    >({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetRecommendedOffers}`,
        body: payload,
      }),
    }),
    getUniqueTPIDs: builder.mutation<IGetUniqueTPIDsResponse, { selectedTPIDs: string[] }>({
      // Response type: Record<string, number[]> — each TPID maps to an array of raidids
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.GetUniqueTPIDs}${getTenantId()}`,
        body: payload.selectedTPIDs,
      }),
    }),
    AONAcrAndRoiDetails: builder.query<IAONAcrAndRoiDetailsResponse, IAONAcrAndRoiDetailsPayload>({
      query: (payload) => ({
        method: 'POST',
        url: `${MANAGE_ACTION_API_CONSTANTS.AONAcrAndRoiDetails}`,
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetRaidEntityDataQuery,
  useUpdateRaidEntityDataMutation,
  useUploadArtemisAttachmentsMutation,
  useGetUploadSasUrlMutation,
  useGetDownloadSasUrlMutation,
  useDownloadArtemisAttachmentMutation,
  useCreateRaidCommentMutation,
  useGetCommentsQuery,
  useUpdateRaidCommentsMutation,
  useDeleteRaidCommentsMutation,
  useGetRaidHistoryQuery,
  // useUpdateCommentsQuery,
  // useDeleteComments,
  useGetArtemisTagsQuery,
  useLazyGetArtemisTagsQuery,
  useGetRaidItemByIdQuery,
  useLazyGetRaidItemByIdQuery,
  useGetIsRestrictedUserQuery,
  useGetWorkloadsQuery,
  useGetRecommendedOffersMutation,
  useGetUniqueTPIDsMutation,
  useAONAcrAndRoiDetailsQuery,
} = getRaidEntityApi;
// Generated by GitHub Copilot | Updated on: 2026-06-18 | 55 lines modified | Added typed interfaces for AONAcrAndRoiDetails response
// Generated by GitHub Copilot | Updated on: 2026-06-18 | 1 lines modified | Typed IncrementalRevenueThreeYear as Record<string, number>
// Generated by GitHub Copilot | Updated on: 2026-07-23 | 24 lines modified | Added FactoryRecommendedSplitDetails query endpoint + payload/response interfaces + lazy hook
// Generated by GitHub Copilot | Updated on: 2026-10-07 | 2 lines modified | Added updated project start date response field
// Generated by GitHub Copilot | Updated on: 2026-11-02 | 1 line modified | Encode tag search query values
// Copilot | 2026-11-05 | Port Task 154096 encoded tag query values to migrated UI paths.
